import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Card,
  CardBody,
  Col,
  Container,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  Alert,
} from "reactstrap"
import classnames from "classnames"
import Breadcrumbs from "components/Common/Breadcrumb"
import { GetMyParts, CreateTraderRequest } from "helpers/backend_helper"
import { useLoading } from "helpers/custom_hooks"
import { IsTraderCompany } from "helpers/misc_helpers"

const NAV_LABELS = ["Request Details", "Select Parts", "Confirm & Submit"]

const NewTraderRequest = () => {
  const navigate = useNavigate()
  const setGlobalLoading = useLoading()

  const [activeTab, setActiveTab] = useState(1)
  const [passedSteps, setPassedSteps] = useState([1])
  const [error, setError] = useState("")

  // Step 1 state
  const [title, setTitle] = useState("")
  const [notes, setNotes] = useState("")

  // Step 2 state
  const [myParts, setMyParts] = useState([])
  const [selectedPartIDs, setSelectedPartIDs] = useState([])
  const [partsLoaded, setPartsLoaded] = useState(false)
  const [partQuantities, setPartQuantities] = useState({})
  const [partDatecodes, setPartDatecodes] = useState({})
  const [bulkDatecode, setBulkDatecode] = useState("")

  const DATECODE_OPTIONS = ["<2 years", "<5 years", ">5 years"]

  // Redirect Traders away — they cannot create requests
  useEffect(() => {
    if (IsTraderCompany()) {
      navigate("/trader-requests")
    }
  }, [])

  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab)
      if (!passedSteps.includes(tab)) {
        setPassedSteps(prev => [...prev, tab])
      }
    }
  }

  const loadMyParts = async () => {
    if (partsLoaded) return
    setGlobalLoading(true)
    try {
      const data = await GetMyParts()
      setMyParts(data || [])
      const quantities = {}
      ;(data || []).forEach(p => { quantities[p.id] = p.quantity })
      setPartQuantities(quantities)
      setPartsLoaded(true)
    } catch (err) {
      console.error("Error loading parts:", err)
    }
    setGlobalLoading(false)
  }

  const handleStep1Next = () => {
    if (!title.trim()) {
      setError("Please enter a title.")
      return
    }
    setError("")
    loadMyParts()
    toggleTab(2)
  }

  const handleStep2Next = () => {
    if (selectedPartIDs.length === 0) {
      setError("Please select at least one part.")
      return
    }
    setError("")
    toggleTab(3)
  }

  const togglePartSelection = partID => {
    setSelectedPartIDs(prev =>
      prev.includes(partID) ? prev.filter(id => id !== partID) : [...prev, partID]
    )
  }

  const toggleSelectAll = () => {
    if (selectedPartIDs.length === myParts.length) {
      setSelectedPartIDs([])
    } else {
      setSelectedPartIDs(myParts.map(p => p.id))
    }
  }

  const handleBulkDatecode = value => {
    setBulkDatecode(value)
    if (value) {
      const codes = {}
      myParts.forEach(p => { codes[p.id] = value })
      setPartDatecodes(codes)
    }
  }

  const handleRowDatecode = (partId, value) => {
    setPartDatecodes(prev => ({ ...prev, [partId]: value }))
    setBulkDatecode("")
  }

  const handleRowQuantity = (partId, value) => {
    const num = parseInt(value, 10)
    setPartQuantities(prev => ({ ...prev, [partId]: isNaN(num) || num < 1 ? 1 : num }))
  }

  const getSelectedParts = () =>
    myParts.filter(p => selectedPartIDs.includes(p.id))

  const handleSubmit = async () => {
    setError("")
    setGlobalLoading(true)
    try {
      const parts = getSelectedParts().map(p => ({
        partID: p.id,
        partNumber: p.partNumber,
        manufacturer: p.manufacturer,
        quantity: partQuantities[p.id] ?? p.quantity,
        datecode: partDatecodes[p.id] || null,
        internalPart: p.internalPart,
        description: p.description,
        notes: p.notes,
        currency: p.currency,
      }))

      const result = await CreateTraderRequest({ title, notes, parts })
      navigate(`/trader-requests/${result.id}`)
    } catch (err) {
      console.error("Error creating trader request:", err)
      setError("Failed to create request. Please try again.")
    }
    setGlobalLoading(false)
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Excess Price Check"
          breadcrumbItem="New Excess Price Check"
        />
        <Row className="justify-content-center">
          <Col lg={10}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">New Excess Price Check</h4>

                {error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {/* Wizard Navigation */}
                <div className="wizard clearfix">
                  <div className="steps clearfix">
                    <ul>
                      {NAV_LABELS.map((label, index) => (
                        <NavItem
                          key={index}
                          className={classnames({
                            current: activeTab === index + 1,
                          })}
                        >
                          <NavLink
                            className={classnames({
                              current: activeTab === index + 1,
                            })}
                            onClick={() => {
                              if (passedSteps.includes(index + 1)) {
                                toggleTab(index + 1)
                              }
                            }}
                            disabled={!passedSteps.includes(index + 1)}
                          >
                            <span className="number">{index + 1}.</span>{" "}
                            {label}
                          </NavLink>
                        </NavItem>
                      ))}
                    </ul>
                  </div>

                  <div className="content clearfix mt-4">
                    <TabContent activeTab={activeTab}>

                      {/* ── Step 1: Request Details ── */}
                      <TabPane tabId={1}>
                        <Form>
                          <FormGroup row>
                            <Label sm={3}>Title *</Label>
                            <Col sm={9}>
                              <Input
                                type="text"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                placeholder="e.g. Q2 Excess Stock Valuation"
                              />
                            </Col>
                          </FormGroup>
                          <FormGroup row>
                            <Label sm={3}>Notes</Label>
                            <Col sm={9}>
                              <Input
                                type="textarea"
                                rows={3}
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                placeholder="Optional notes for traders..."
                              />
                            </Col>
                          </FormGroup>
                        </Form>
                        <div className="d-flex justify-content-end mt-3">
                          <Button color="primary" onClick={handleStep1Next}>
                            Next <i className="bx bx-right-arrow-alt ms-1"></i>
                          </Button>
                        </div>
                      </TabPane>

                      {/* ── Step 2: Select Parts ── */}
                      <TabPane tabId={2}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <p className="text-muted mb-0">
                            Select the parts from your inventory to include in this request.
                          </p>
                          <Button
                            color="link"
                            size="sm"
                            onClick={toggleSelectAll}
                          >
                            {selectedPartIDs.length === myParts.length
                              ? "Deselect All"
                              : "Select All"}
                          </Button>
                        </div>
                        {myParts.length === 0 ? (
                          <Alert color="warning">
                            No parts found. Please upload parts first via{" "}
                            <a href="/parts-upload">Parts Upload</a>.
                          </Alert>
                        ) : (
                          <div className="table-responsive" style={{ maxHeight: 400 }}>
                            <Table className="table align-middle table-nowrap table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: 40 }}></th>
                                  <th>Part Number</th>
                                  <th>Manufacturer</th>
                                  <th style={{ width: 110 }}>Quantity</th>
                                  <th>
                                    <div className="d-flex flex-column gap-1">
                                      <span>Datecode</span>
                                      <Input
                                        type="select"
                                        bsSize="sm"
                                        value={bulkDatecode}
                                        onChange={e => handleBulkDatecode(e.target.value)}
                                        onClick={e => e.stopPropagation()}
                                        style={{ minWidth: 110, fontWeight: "normal" }}
                                      >
                                        <option value="">Set all…</option>
                                        {DATECODE_OPTIONS.map(opt => (
                                          <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                      </Input>
                                    </div>
                                  </th>
                                  <th>Internal Part</th>
                                </tr>
                              </thead>
                              <tbody>
                                {myParts.map(part => (
                                  <tr
                                    key={part.id}
                                    onClick={() => togglePartSelection(part.id)}
                                    style={{ cursor: "pointer" }}
                                    className={
                                      selectedPartIDs.includes(part.id)
                                        ? "table-primary"
                                        : ""
                                    }
                                  >
                                    <td>
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={selectedPartIDs.includes(part.id)}
                                        onChange={() => togglePartSelection(part.id)}
                                        onClick={e => e.stopPropagation()}
                                      />
                                    </td>
                                    <td>{part.partNumber}</td>
                                    <td>{part.manufacturer}</td>
                                    <td>
                                      <Input
                                        type="number"
                                        bsSize="sm"
                                        min={1}
                                        value={partQuantities[part.id] ?? part.quantity}
                                        onChange={e => handleRowQuantity(part.id, e.target.value)}
                                        onClick={e => e.stopPropagation()}
                                        style={{ width: 90 }}
                                      />
                                    </td>
                                    <td>
                                      <Input
                                        type="select"
                                        bsSize="sm"
                                        value={partDatecodes[part.id] || ""}
                                        onChange={e => handleRowDatecode(part.id, e.target.value)}
                                        onClick={e => e.stopPropagation()}
                                        style={{ minWidth: 110 }}
                                      >
                                        <option value="">Select…</option>
                                        {DATECODE_OPTIONS.map(opt => (
                                          <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                      </Input>
                                    </td>
                                    <td>{part.internalPart || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}
                        <p className="text-muted mt-2">
                          {selectedPartIDs.length} of {myParts.length} parts selected
                        </p>
                        <div className="d-flex justify-content-between mt-3">
                          <Button
                            color="secondary"
                            outline
                            onClick={() => toggleTab(1)}
                          >
                            <i className="bx bx-left-arrow-alt me-1"></i> Back
                          </Button>
                          <Button color="primary" onClick={handleStep2Next}>
                            Next <i className="bx bx-right-arrow-alt ms-1"></i>
                          </Button>
                        </div>
                      </TabPane>

                      {/* ── Step 3: Confirm & Submit ── */}
                      <TabPane tabId={3}>
                        <h5 className="mb-3">Summary</h5>
                        <table className="table table-bordered table-sm mb-4">
                          <tbody>
                            <tr>
                              <td className="fw-bold" style={{ width: "30%" }}>
                                Title
                              </td>
                              <td>{title}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Parts selected</td>
                              <td>{selectedPartIDs.length}</td>
                            </tr>
                            {notes && (
                              <tr>
                                <td className="fw-bold">Notes</td>
                                <td>{notes}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        <h6 className="mb-2">Selected Parts</h6>
                        <div
                          className="table-responsive mb-4"
                          style={{ maxHeight: 300 }}
                        >
                          <Table className="table table-sm table-bordered align-middle">
                            <thead className="table-light">
                              <tr>
                                <th>Part Number</th>
                                <th>Manufacturer</th>
                                <th>Quantity</th>
                                <th>Datecode</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getSelectedParts().map(p => (
                                <tr key={p.id}>
                                  <td>{p.partNumber}</td>
                                  <td>{p.manufacturer}</td>
                                  <td>{partQuantities[p.id] ?? p.quantity}</td>
                                  <td>{partDatecodes[p.id] || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        <Alert color="info">
                          <i className="bx bx-info-circle me-1"></i>
                          This request will be sent <strong>anonymously</strong> to
                          all Trader companies. Your identity will only be revealed
                          after you select an offer.
                        </Alert>

                        <div className="d-flex justify-content-between mt-3">
                          <Button
                            color="secondary"
                            outline
                            onClick={() => toggleTab(2)}
                          >
                            <i className="bx bx-left-arrow-alt me-1"></i> Back
                          </Button>
                          <Button color="success" onClick={handleSubmit}>
                            <i className="bx bx-send me-1"></i> Submit Request
                          </Button>
                        </div>
                      </TabPane>
                    </TabContent>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default NewTraderRequest
